import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Battery, Feather, Gauge, Droplets } from "lucide-react";
import { SPECS } from "@/lib/product-data";

const glass = "bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-2xl";
const glassHover = "hover:bg-white/[0.06] hover:border-white/15 hover:-translate-y-1 transition-all duration-300";
const anim = { hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } };

function AnimatedNumber({ target, suffix = "", isFloat = false }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const ran = useRef(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !ran.current) {
        ran.current = true;
        let s = 0;
        const inc = target / 45;
        const step = () => {
          s += inc;
          if (s >= target) { setVal(target); return; }
          setVal(isFloat ? parseFloat(s.toFixed(1)) : Math.floor(s));
          requestAnimationFrame(step);
        };
        step();
        obs.disconnect();
      }
    }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target, isFloat]);
  return <span ref={ref}>{isFloat ? val.toFixed(1) : val.toLocaleString()}{suffix}</span>;
}

export function SocialProofBar() {
  const stats = [
    { num: 50000, suffix: "+", label: "Fans Sold", isFloat: false },
    { num: 4.8, suffix: "", label: "Star Rating", isFloat: true },
    { num: 2341, suffix: "", label: "5-Star Reviews", isFloat: false },
    { num: 30, suffix: "-Day", label: "Guarantee", isFloat: false },
  ];
  return (
    <section data-testid="social-proof-bar" className="py-16 relative">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className={`${glass} p-8 md:p-12`}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {stats.map((s, i) => (
              <div key={i}>
                <div className="font-heading text-3xl sm:text-4xl font-bold text-cyan-400">
                  <AnimatedNumber target={s.num} suffix={s.suffix} isFloat={s.isFloat} />
                </div>
                <div className="text-xs uppercase tracking-[0.15em] text-slate-500 mt-2 font-semibold">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function BentoFeatures() {
  const features = [
    { Icon: Droplets, title: "360° Bladeless Airflow", desc: "Turbine-driven cooling wraps around your neck. 48 micro-outlets deliver smooth, even airflow.", span: "md:col-span-8" },
    { Icon: Battery, title: "1500mAh Power", desc: "USB-C fast charging. 2-6 hours runtime.", span: "md:col-span-4" },
    { Icon: Gauge, title: "Whisper Quiet", desc: "Under 25dB on low — quieter than a library.", span: "md:col-span-4" },
    { Icon: Feather, title: "Ultra-Light Build", desc: "Medical-grade ABS with flexible silicone band. Only 400g — so light you'll forget it's there.", span: "md:col-span-8" },
  ];
  return (
    <motion.section data-testid="bento-features" className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={anim}>
      <div className="text-center mb-14">
        <span className="text-xs font-bold tracking-[0.2em] uppercase text-cyan-400 block mb-3">Engineered for Cool</span>
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">Built Different</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
        {features.map((f, i) => (
          <div key={i} className={`${f.span} ${glass} ${glassHover} p-8 group cursor-default`}>
            <div className="w-12 h-12 rounded-xl bg-cyan-400/10 flex items-center justify-center mb-4 group-hover:bg-cyan-400/20 transition-colors">
              <f.Icon size={24} className="text-cyan-400" />
            </div>
            <h3 className="font-heading text-xl font-semibold mb-2">{f.title}</h3>
            <p className="text-sm text-slate-400 leading-relaxed max-w-md">{f.desc}</p>
          </div>
        ))}
      </div>
    </motion.section>
  );
}

const SPEC_ICONS = [Battery, Feather, Gauge, Droplets];

export function SpecsGrid() {
  return (
    <motion.section data-testid="specs-grid" className="max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={anim}>
      <div className="text-center mb-14">
        <span className="text-xs font-bold tracking-[0.2em] uppercase text-cyan-400 block mb-3">Technical Details</span>
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">Built for Performance</h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-5 max-w-4xl mx-auto">
        {SPECS.map((s, i) => {
          const Icon = SPEC_ICONS[i];
          return (
            <div key={i} className={`${glass} ${glassHover} p-6 text-center cursor-default`}>
              <Icon size={24} className="mx-auto mb-3 text-cyan-400" />
              <div className="text-xs text-slate-500 font-bold uppercase tracking-[0.1em] mb-1">{s.label}</div>
              <div className="text-2xl sm:text-3xl font-bold tracking-tight">{s.value}</div>
              <div className="text-sm text-slate-400 mt-1">{s.detail}</div>
            </div>
          );
        })}
      </div>
    </motion.section>
  );
}
